import React from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { isEqual } from 'lodash';
import { Box } from '@mui/material';

import { LoadingOverlay } from '@occc/ui-design-system';
import { makeZodValidate as makeValidate } from '@occc-app/design-system';
import { FormLayout } from '@occc-app/routes/CampaignComponents/components/FormLayout';
import { CanEditFormFields } from '@occc-app/App/CanEditFormFieldsContext';
import { InAudienceConstraint } from '@occc/feat-constraints';
import { useConfig } from '@occc/config';

import {
  MessagingCampaignTargetFormProps,
  MessagingCampaignTargetFormValues,
} from './target-form.types';
import { TargetFormSchema } from './target-form.schema';
import { SubBrandSelector } from './sub-brand-selector.component';

import { FooterActions } from '../footer-actions/footer-actions.component';
import { MessagingCampaignTabs } from '../../messaging-campaign-configure.constants';

export const TargetForm = ({
  onSubmit,
  onNextStepClick,
  initialValues,
  onCancelClick,
  loading,
  chainId,
  isUserPermittedToEdit,
}: MessagingCampaignTargetFormProps) => {
  const { config } = useConfig();
  const isSubBrandSelectionEnabled = Boolean(config.subBrands?.enabled);

  const validate = makeValidate(TargetFormSchema);
  const renderForm: FormRenderProps<MessagingCampaignTargetFormValues>['render'] =
    React.useCallback(
      ({
        handleSubmit,
        submitting,
        values,
        dirty,
      }: FormRenderProps<MessagingCampaignTargetFormValues>) => {
        const handleCancelClick = () => {
          onCancelClick(dirty);
        };

        const actions = (
          <FooterActions
            onNextStepClick={onNextStepClick}
            onCancel={handleCancelClick}
            isSubmitDisabled={submitting}
            currentTab={MessagingCampaignTabs.Target}
            isUserPermittedToEdit={isUserPermittedToEdit}
          />
        );

        const audienceConstraint = (
          <InAudienceConstraint
            values={values}
            chainId={chainId}
            infoTitle='Audience size'
            isNoTargetingAttributesWarningEnabled={true}
          />
        );

        return (
          <form onSubmit={handleSubmit}>
            <FormLayout actions={actions}>
              <Box p={3}>
                {audienceConstraint}
                {isSubBrandSelectionEnabled && <SubBrandSelector />}
              </Box>
            </FormLayout>
          </form>
        );
      },
      [onNextStepClick, isUserPermittedToEdit, chainId, onCancelClick, isSubBrandSelectionEnabled],
    );

  return (
    <Box position='relative'>
      <CanEditFormFields isEditable={isUserPermittedToEdit}>
        <Form<MessagingCampaignTargetFormValues, Partial<MessagingCampaignTargetFormValues>>
          initialValues={initialValues}
          onSubmit={onSubmit}
          render={renderForm}
          validate={validate}
          initialValuesEqual={isEqual}
          keepDirtyOnReinitialize
        />
      </CanEditFormFields>

      {loading && <LoadingOverlay />}
    </Box>
  );
};
